import { useShopI18n } from '../../i18n'
import { buildFieldUiHelpers } from './customer-auth/CustomerAuthForm.helpers'
import {
  ResetPasswordFormView,
  ResetPasswordStateCard,
  useResetPasswordFlowEffects,
  useResetPasswordFlowReducer,
  useResetPasswordSubmitHandlers,
  useResetPasswordViewModel,
} from './customer-auth/reset-password'

export const CustomerResetPasswordPage = () => {
  const { t, toLocalizedPath } = useShopI18n()
  const [state, dispatch] = useResetPasswordFlowReducer()
  useResetPasswordFlowEffects({ dispatch })

  const viewModel = useResetPasswordViewModel({
    state,
    t,
  })

  const fieldUi = buildFieldUiHelpers({
    fieldErrors: viewModel.fieldErrors,
    onFieldBlur: (field) => {
      if (field === 'password') dispatch({ type: 'SET_PASSWORD_TOUCHED', value: true })
      if (field === 'confirmPassword') dispatch({ type: 'SET_CONFIRM_PASSWORD_TOUCHED', value: true })
    },
  })

  const loginHref = toLocalizedPath('/dashboard/login')
  const forgotHref = toLocalizedPath('/dashboard/forgot-password')

  const { handleSubmit } = useResetPasswordSubmitHandlers({
    dispatch,
    token: state.token,
    password: state.password,
    canSubmit: viewModel.canSubmit,
    messages: {
      submitError: t('customer.auth.resetPassword.submitError'),
    },
  })

  return (
    <div className="ca-page ca-theme-transition py-6 sm:py-10 lg:py-14">
      <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6 lg:px-8">
        <section className="relative">
          <div className="relative grid items-start gap-y-7 gap-x-10 md:gap-x-12 lg:grid-cols-12 lg:gap-x-16 xl:gap-x-20">
            <div className="flex h-full flex-col gap-8 lg:col-span-7 lg:gap-10">
              <div className="space-y-3.5">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-(--color-status-info)">
                  {t('common.customerArea')}
                </p>
                <h1 className="max-w-[20ch] text-balance text-3xl font-semibold tracking-tight text-(--color-text-primary) sm:text-4xl lg:text-[2.6rem]">
                  {viewModel.title}
                </h1>
                <p className="max-w-[62ch] text-sm leading-relaxed text-(--color-text-secondary) sm:text-base">
                  {viewModel.subtitle}
                </p>
              </div>
            </div>

            <div className="w-full lg:col-span-5 lg:justify-self-end lg:max-w-[29rem]">
              <div className="ca-card-shell ca-theme-transition relative overflow-hidden rounded-2xl border-[color-mix(in_srgb,var(--color-border-strong)_60%,var(--color-border-default))] bg-[color-mix(in_srgb,var(--color-bg-card)_94%,var(--color-bg-page-secondary))] px-4 py-4 shadow-[0_24px_48px_-30px_color-mix(in_srgb,var(--color-text-primary)_40%,transparent)] sm:px-5 sm:py-5">
                {state.success ? (
                  <a
                    href={loginHref}
                    className="ca-btn-primary inline-flex h-11 w-full items-center justify-center px-4 text-sm font-semibold shadow-[0_10px_24px_-18px_color-mix(in_srgb,var(--color-action-primary)_85%,transparent)] transition-colors duration-200"
                  >
                    {t('customer.auth.resetPassword.goToLogin')}
                  </a>
                ) : state.pageState === 'loading' ? (
                  <div className="flex h-24 items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-[color-mix(in_srgb,var(--color-border-strong)_65%,transparent)] border-t-transparent" />
                  </div>
                ) : state.pageState === 'expired' ? (
                  <ResetPasswordStateCard
                    title={t('customer.auth.resetPassword.expiredTitle')}
                    description={t('customer.auth.resetPassword.expiredDescription')}
                    actionLabel={t('customer.auth.resetPassword.requestNewLinkCta')}
                    actionHref={forgotHref}
                    secondaryLabel={t('customer.auth.resetPassword.backToLogin')}
                    secondaryHref={loginHref}
                  />
                ) : state.pageState === 'invalid' ? (
                  <ResetPasswordStateCard
                    title={t('customer.auth.resetPassword.invalidTitle')}
                    description={t('customer.auth.resetPassword.invalidDescription')}
                    actionLabel={t('customer.auth.resetPassword.goToLogin')}
                    actionHref={loginHref}
                    secondaryLabel={t('customer.auth.resetPassword.requestNewLinkCta')}
                    secondaryHref={forgotHref}
                  />
                ) : (
                  <ResetPasswordFormView
                    password={state.password}
                    confirmPassword={state.confirmPassword}
                    submitting={state.submitting}
                    formError={state.formError}
                    loginHref={loginHref}
                    submitCta={t('customer.auth.resetPassword.submitCta')}
                    submittingCta={t('customer.auth.resetPassword.submittingCta')}
                    backToLoginLabel={t('customer.auth.resetPassword.backToLogin')}
                    passwordLabel={t('customer.auth.password')}
                    passwordPlaceholder={t('customer.auth.passwordPlaceholder')}
                    passwordShowLabel={t('customer.auth.password.show')}
                    passwordHideLabel={t('customer.auth.password.hide')}
                    confirmPasswordLabel={t('customer.auth.passwordConfirm')}
                    confirmPasswordPlaceholder={t('customer.auth.passwordConfirmPlaceholder')}
                    fieldUi={fieldUi}
                    onPasswordChange={(value) => dispatch({ type: 'SET_PASSWORD', value })}
                    onConfirmPasswordChange={(value) => dispatch({ type: 'SET_CONFIRM_PASSWORD', value })}
                    onSubmit={handleSubmit}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
